<template>
  <v-slide-y-reverse-transition>
    <v-card
      v-if="value"
      class="floating-notif-card elevation-12"
      :style="cardStyle"
    >
      <!-- Header -->
      <div
        class="notif-header"
        :class="{ 'expanded': internalExpanded }"
        :style="headerStyle"
        @click.stop="toggleExpand"
      >
        <div class="header-main">
          <div class="header-icon-container">
            <v-icon color="white" size="22">{{ icon }}</v-icon>
          </div>
          <div class="header-text-container">
            <span class="header-title">{{ title }}</span>
            <v-chip
              x-small
              color="rgba(255,255,255,0.2)"
              text-color="white"
              class="ml-2 px-2"
            >
              {{ items.length }}
            </v-chip>
          </div>
        </div>
        <div class="header-actions">
          <v-btn
            v-if="showAllButton"
            icon
            x-small
            title="Ver todo"
            class="action-icon-btn mr-1"
            @click.stop="$emit('click:all')"
          >
            <v-icon color="white">mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            class="action-icon-btn"
            @click.stop="toggleExpand"
          >
            <v-icon color="white">{{
              internalExpanded ? "mdi-chevron-down" : "mdi-chevron-up"
            }}</v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            class="action-icon-btn"
            @click.stop="$emit('input', false)"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Content -->
      <v-expand-transition>
        <div v-show="internalExpanded" class="notif-content">
          <v-list dense class="pa-0 background-transparent">
            <v-list-item
              v-for="(item, index) in items"
              :key="index"
              class="px-3 py-2 notif-item"
              @click="$emit('click:item', item)"
            >
              <v-list-item-content>
                <div class="d-flex justify-space-between align-start mb-1">
                  <span class="notif-item-title">{{ item[itemTitleKey] }}</span>
                  <div class="d-flex align-center">
                    <v-chip
                      v-if="item[itemChipKey] !== undefined"
                      :color="getChipColor(item[itemChipKey], item)"
                      x-small
                      label
                      class="white--text days-chip mr-2"
                    >
                      {{ formatChipText(item[itemChipKey], item) }}
                    </v-chip>
                    <!-- Botón para descartar individualmente -->
                    <v-btn
                      icon
                      x-small
                      class="dismiss-btn"
                      title="Marcar como leído"
                      @click.stop="$emit('click:dismiss', item)"
                    >
                      <v-icon size="14" color="grey darken-1">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div class="notif-item-subtitle">
                  {{ item[itemSubtitleKey] }}
                </div>
                
                <!-- Footer Info -->
                <div class="d-flex align-center mt-2 notif-item-footer">
                  <template v-if="item[itemFooterLeftKey]">
                    <v-icon x-small class="mr-1 grey--text text--darken-1">{{ footerLeftIcon }}</v-icon>
                    <span>{{ item[itemFooterLeftKey] }}</span>
                  </template>
                  <v-spacer></v-spacer>
                  <template v-if="item[itemFooterRightKey]">
                    <v-icon x-small class="mr-1 grey--text text--darken-1">{{ footerRightIcon }}</v-icon>
                    <span>{{ formatFooterRight(item[itemFooterRightKey]) }}</span>
                  </template>
                </div>
              </v-list-item-content>
            </v-list-item>
            
            <v-list-item v-if="items.length === 0" class="justify-center py-4 grey--text">
              No hay avisos pendientes
            </v-list-item>
          </v-list>
        </div>
      </v-expand-transition>
    </v-card>
  </v-slide-y-reverse-transition>
</template>

<script>
export default {
  name: 'SFloatingNotification',
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: 'Notificaciones' },
    icon: { type: String, default: 'mdi-bell' },
    items: { type: Array, default: () => [] },
    expanded: { type: Boolean, default: false },
    showAllButton: { type: Boolean, default: true },
    
    // Configuración de visualización
    gradient: { type: String, default: 'linear-gradient(135deg, #1b5e20 0%, #43a047 100%)' },
    bottomOffset: { type: Number, default: 0 },
    rightOffset: { type: Number, default: 0 },
    isStacked: { type: Boolean, default: true }, // Por defecto ahora lo manejaremos en stack
    
    // Mapeo de llaves de datos
    itemTitleKey: { type: String, default: 'title' },
    itemSubtitleKey: { type: String, default: 'subtitle' },
    itemChipKey: { type: String, default: 'chipValue' },
    itemFooterLeftKey: { type: String, default: 'footerLeft' },
    itemFooterRightKey: { type: String, default: 'footerRight' },
    
    // Iconos de footer
    footerLeftIcon: { type: String, default: 'mdi-map-marker' },
    footerRightIcon: { type: String, default: 'mdi-calendar-clock' },

    // Funciones personalizadas para chips y formato
    chipColorFunction: { type: Function, default: null },
    chipTextFunction: { type: Function, default: null },
    footerRightFunction: { type: Function, default: null }
  },
  data() {
    return {
      internalExpanded: this.expanded
    }
  },
  watch: {
    expanded(val) {
      this.internalExpanded = val;
    }
  },
  computed: {
    cardStyle() {
      if (this.isStacked) {
        return {
          marginBottom: '12px',
          position: 'relative'
        };
      }
      return {
        bottom: `${this.bottomOffset}px`,
        right: `${this.rightOffset}px`,
        position: 'fixed'
      };
    },
    headerStyle() {
      return {
        background: this.gradient
      };
    }
  },
  methods: {
    toggleExpand() {
      this.internalExpanded = !this.internalExpanded;
      this.$emit('update:expanded', this.internalExpanded);
    },
    getChipColor(value, item) {
      if (this.chipColorFunction) return this.chipColorFunction(value, item);
      
      // Default logic for days to expire
      if (value <= 15) return "error";
      if (value <= 30) return "warning";
      if (value <= 60) return "success";
      return "info";
    },
    formatChipText(value, item) {
      if (this.chipTextFunction) return this.chipTextFunction(value, item);
      
      // Default logic for days to expire
      if (value <= 0) return "Vencido";
      return value + " d";
    },
    formatFooterRight(value) {
      if (this.footerRightFunction) return this.footerRightFunction(value);
      return value;
    }
  }
}
</script>

<style scoped>
.floating-notif-card {
  width: 360px;
  z-index: 10000;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease-in-out;
}

.notif-header {
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  user-select: none;
}

.notif-header:hover {
  filter: brightness(1.1);
}

.notif-header.expanded {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.header-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon-container {
  background: rgba(255, 255, 255, 0.2);
  padding: 6px;
  border-radius: 10px;
  display: flex;
  box-shadow: inset 0 0 10px rgba(255, 255, 255, 0.1);
}

.header-title {
  color: white;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.header-actions {
  display: flex;
  gap: 6px;
}

.action-icon-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(4px);
  transition: transform 0.2s;
}

.action-icon-btn:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.25) !important;
}

.notif-content {
  overflow-y: auto;
  max-height: 400px;
  background: transparent;
}

.notif-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  cursor: pointer;
}

.notif-item:hover {
  background: rgba(59, 130, 246, 0.05);
  transform: translateX(4px);
}

.notif-item:last-child {
  border-bottom: none;
}

.dismiss-btn {
  transition: all 0.2s ease;
  opacity: 0.7;
}

.dismiss-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  transform: rotate(90deg);
  opacity: 1;
}

.notif-item-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #000000;
  line-height: 1.3;
}

.days-chip {
  font-weight: 800 !important;
  font-size: 10px !important;
  height: 22px !important;
  border-radius: 6px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.notif-item-subtitle {
  font-size: 0.85rem;
  color: #2c2c2c;
  margin-top: 4px;
  font-weight: 600;
}

.notif-item-footer {
  font-size: 0.75rem;
  color: #4b5563;
  font-weight: 700;
}

.background-transparent {
  background: transparent !important;
}

/* Scrollbar */
.notif-content::-webkit-scrollbar {
  width: 5px;
}
.notif-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.02);
}
.notif-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}
</style>
